import type { DirectiveBinding } from 'vue'

export type LoadingBinding = {
    toggle: boolean
    offsetTop?: number
    removeParentPosition?: boolean
}

const BLOCK_KEY = '__uiDirectiveLoadingBlock' as const

const isActive = (binding: DirectiveBinding<boolean | LoadingBinding>): boolean => {
    const v = binding.value
    if (v == null || v === false) {
        return false
    }
    if (typeof v === 'boolean') {
        return v
    }
    return Boolean(v.toggle)
}

const offsetTop = (binding: DirectiveBinding<boolean | LoadingBinding>): number | undefined => {
    const v = binding.value
    if (v && typeof v === 'object' && 'offsetTop' in v) {
        return v.offsetTop
    }
    return undefined
}

const show = (el: HTMLElement, binding: DirectiveBinding<boolean | LoadingBinding>): void => {
    if (!isActive(binding)) {
        hide(el)
        return
    }

    el.classList.add('ui-directive__loading-parent')

    const v = binding.value
    if (v && typeof v === 'object' && v.removeParentPosition) {
        el.classList.add('--remove-position')
    } else {
        el.classList.remove('--remove-position')
    }

    let block = (el as HTMLElement & { [BLOCK_KEY]?: HTMLElement })[BLOCK_KEY]
    if (!block || !el.contains(block)) {
        block = document.createElement('div')
        const line = document.createElement('div')
        block.className = 'ui-directive__loading-children'
        line.className = 'ui-directive__loading-line'
        block.appendChild(line)
        el.appendChild(block)
        ;(el as HTMLElement & { [BLOCK_KEY]?: HTMLElement })[BLOCK_KEY] = block
    }

    const top = offsetTop(binding)
    if (top != null) {
        block.style.top = `${top}px`
    } else {
        block.style.top = ''
    }
}

const hide = (el: HTMLElement): void => {
    const block = (el as HTMLElement & { [BLOCK_KEY]?: HTMLElement })[BLOCK_KEY]
    if (block?.parentNode === el) {
        el.removeChild(block)
    }
    delete (el as HTMLElement & { [BLOCK_KEY]?: HTMLElement })[BLOCK_KEY]
    el.classList.remove('ui-directive__loading-parent', '--remove-position')
}

export const Loading = {
    bind(el: HTMLElement, binding: DirectiveBinding<boolean | LoadingBinding>) {
        show(el, binding)
    },
    update(el: HTMLElement, binding: DirectiveBinding<boolean | LoadingBinding>) {
        show(el, binding)
    },
    unbind(el: HTMLElement) {
        hide(el)
    },
}
